import {
  Directive,
  Input,
  ElementRef,
  Renderer2,
  HostListener,
  ComponentRef,
  ViewContainerRef,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import { UmssTooltipComponent } from '../components/tooltip/tooltip.component';

@Directive({
  selector: '[umssTooltip]',
  standalone: true
})
export class UmssTooltipDirective implements OnDestroy, AfterViewInit {
  @Input('umssTooltip') tooltipText: string = '';
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
  @Input() tooltipVariant: 'dark' | 'light' = 'dark';
  @Input() tooltipSize: 'small' | 'medium' | 'large' = 'medium';
  @Input() tooltipDelay: number = 300;

  private tooltipComponent: ComponentRef<UmssTooltipComponent> | null = null;
  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;
  private observer: MutationObserver | null = null;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngAfterViewInit(): void {
    this.fixDisabledPointerEvents();

    this.observer = new MutationObserver(() => {
      this.fixDisabledPointerEvents();
    });

    this.observer.observe(this.elementRef.nativeElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['disabled', 'data-disabled']
    });
  }

  private fixDisabledPointerEvents(): void {
    const el = this.elementRef.nativeElement as HTMLElement;

    const applyFix = (target: HTMLElement) => {
      if (target.hasAttribute('disabled')) {
        if (target.getAttribute('data-tooltip-disabled') !== 'true') {
          // If Angular applied the disabled property, ensure our tracking attributes are present
          this.renderer.removeAttribute(target, 'disabled');
          this.renderer.setAttribute(target, 'disabled', 'true');
          this.renderer.setAttribute(target, 'aria-disabled', 'true');
          this.renderer.setAttribute(target, 'data-tooltip-disabled', 'true');
        }
      } else {
        // If the disabled attribute is naturally missing (Angular evaluated it to false), clean up!
        if (target.getAttribute('data-tooltip-disabled') === 'true') {
          this.renderer.removeAttribute(target, 'aria-disabled');
          this.renderer.removeAttribute(target, 'data-tooltip-disabled');
          this.renderer.removeAttribute(target, 'data-disabled');
        }
      }

      // Ensure pointer-events are cleaned up when the element is truly NOT disabled
      if (
        target.getAttribute('aria-disabled') === 'true' ||
        target.getAttribute('data-disabled') === 'true' ||
        target.getAttribute('data-tooltip-disabled') === 'true' ||
        target.hasAttribute('disabled')
      ) {
        // Apply pointer-events auto ONLY if a tooltip text actually exists to need it,
        // otherwise regular disabled styling will apply normally to the CSS pointer
        this.renderer.setStyle(target, 'pointer-events', 'auto', 2);
      } else {
        this.renderer.removeStyle(target, 'pointer-events');
      }
    };

    if (el.tagName.toLowerCase() === 'button') {
      applyFix(el);
    }

    // Apply to children buttons (e.g in custom Angular wrappers like umss-button)
    el.querySelectorAll('button').forEach((btn) =>
      applyFix(btn as HTMLElement)
    );
  }

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    const el = this.elementRef.nativeElement as HTMLElement;

    // Prevent physical clicks if the element is disabled, or wrapping a disabled element.
    const isVirtuallyDisabled =
      el.getAttribute('data-tooltip-disabled') === 'true' ||
      el.getAttribute('aria-disabled') === 'true' ||
      el.getAttribute('data-disabled') === 'true' ||
      el.getAttribute('disabled') === 'true' ||
      el.hasAttribute('disabled') ||
      el.querySelector('button[data-tooltip-disabled="true"]') !== null ||
      el.querySelector('button[disabled]') !== null ||
      el.querySelector('button[aria-disabled="true"]') !== null;

    if (isVirtuallyDisabled) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    this.showTimeout = setTimeout(() => {
      this.showTooltip();
    }, this.tooltipDelay);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }

    this.hideTimeout = setTimeout(() => {
      this.hideTooltip();
    }, 0); // Immediate hide for better responsiveness
  }

  @HostListener('focus')
  onFocus(): void {
    this.showTooltip();
  }

  @HostListener('blur')
  onBlur(): void {
    this.hideTooltip();
  }

  private showTooltip(): void {
    if (!this.tooltipText || this.tooltipComponent) {
      return;
    }

    // Create component using ViewContainerRef (more reliable)
    this.tooltipComponent =
      this.viewContainerRef.createComponent(UmssTooltipComponent);

    // Set tooltip properties
    this.tooltipComponent.instance.text = this.tooltipText;
    this.tooltipComponent.instance.position = this.tooltipPosition;
    this.tooltipComponent.instance.variant = this.tooltipVariant;
    this.tooltipComponent.instance.size = this.tooltipSize;
    this.tooltipComponent.instance.visible = true;
    this.tooltipComponent.instance.disabled = false;

    // Get the tooltip element
    const tooltipElement = this.tooltipComponent.location.nativeElement;

    // ARBITRARY: Initial styles to prevent shake/flash before positioning
    this.renderer.setStyle(tooltipElement, 'position', 'fixed', 2);
    this.renderer.setStyle(tooltipElement, 'z-index', '1000', 2);
    this.renderer.setStyle(tooltipElement, 'visibility', 'hidden', 2);
    this.renderer.setStyle(tooltipElement, 'pointer-events', 'none', 2); // No hover persistence
    this.renderer.setStyle(tooltipElement, 'top', '0', 2);
    this.renderer.setStyle(tooltipElement, 'left', '0', 2);

    // Append to body
    document.body.appendChild(tooltipElement);

    // Initial detection to ensure DOM is created
    this.tooltipComponent.changeDetectorRef.detectChanges();

    // Position the tooltip after DOM update
    setTimeout(() => {
      this.positionTooltip();
    }, 0);
  }

  private hideTooltip(): void {
    if (this.tooltipComponent) {
      const tooltipElement = this.tooltipComponent.location.nativeElement;
      if (tooltipElement && tooltipElement.parentNode) {
        this.renderer.removeChild(document.body, tooltipElement);
      }
      this.tooltipComponent.destroy();
      this.tooltipComponent = null;
    }

    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private positionTooltip(): void {
    if (!this.tooltipComponent) return;

    const hostElement = this.elementRef.nativeElement;
    const tooltipElement = this.tooltipComponent.location.nativeElement;

    const tooltipContentDiv = tooltipElement.querySelector(
      '.tooltip'
    ) as HTMLElement;
    if (!tooltipContentDiv) {
      // If not found yet, try again after another detection
      this.tooltipComponent.changeDetectorRef.detectChanges();
      const retryDiv = tooltipElement.querySelector('.tooltip') as HTMLElement;
      if (!retryDiv) {
        return;
      }
    }

    this.renderer.setStyle(tooltipElement, 'display', 'block', 2);
    this.renderer.setStyle(tooltipElement, 'margin', '0', 2);
    this.renderer.setStyle(tooltipElement, 'padding', '0', 2);
    this.renderer.setStyle(tooltipContentDiv, 'position', 'relative', 2);
    this.renderer.setStyle(tooltipContentDiv, 'display', 'block', 2);

    let actualHostElement = hostElement;
    if (hostElement.tagName.toLowerCase().startsWith('umss-')) {
      const firstChild =
        hostElement.querySelector('button, span, div, a') ||
        hostElement.firstElementChild;
      if (firstChild) {
        actualHostElement = firstChild as HTMLElement;
      }
    }

    const hostRect = actualHostElement.getBoundingClientRect();

    // Ensure we have layout before measuring
    this.tooltipComponent.changeDetectorRef.detectChanges();
    const tooltipRect = tooltipContentDiv.getBoundingClientRect();

    // Fallback if dimensions are 0 (shouldn't happen with detectChanges)
    const tooltipWidth = tooltipRect.width || 120;
    const tooltipHeight = tooltipRect.height || 32;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8;

    let bestPosition = this.tooltipPosition;

    // Check if preferred position fits, if not, try opposite
    if (
      this.tooltipPosition === 'top' &&
      hostRect.top - tooltipHeight - margin < 0
    ) {
      if (hostRect.bottom + tooltipHeight + margin <= viewportHeight) {
        bestPosition = 'bottom';
      }
    } else if (
      this.tooltipPosition === 'bottom' &&
      hostRect.bottom + tooltipHeight + margin > viewportHeight
    ) {
      if (hostRect.top - tooltipHeight - margin >= 0) {
        bestPosition = 'top';
      }
    } else if (
      this.tooltipPosition === 'left' &&
      hostRect.left - tooltipWidth - margin < 0
    ) {
      if (hostRect.right + tooltipWidth + margin <= viewportWidth) {
        bestPosition = 'right';
      }
    } else if (
      this.tooltipPosition === 'right' &&
      hostRect.right + tooltipWidth + margin > viewportWidth
    ) {
      if (hostRect.left - tooltipWidth - margin >= 0) {
        bestPosition = 'left';
      }
    }

    // Update component's internal position for arrow styling
    this.tooltipComponent.instance.position = bestPosition;
    this.tooltipComponent.changeDetectorRef.detectChanges();
    this.renderer.setAttribute(
      tooltipContentDiv,
      'data-position',
      bestPosition
    );

    let top: number;
    let left: number;
    let transform: string;

    switch (bestPosition) {
      case 'top':
        top = hostRect.top - margin;
        left = hostRect.left + hostRect.width / 2;
        transform = 'translate(-50%, -100%)';
        break;
      case 'bottom':
        top = hostRect.bottom + margin;
        left = hostRect.left + hostRect.width / 2;
        transform = 'translate(-50%, 0)';
        break;
      case 'left':
        top = hostRect.top + hostRect.height / 2;
        left = hostRect.left - margin;
        transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        top = hostRect.top + hostRect.height / 2;
        left = hostRect.right + margin;
        transform = 'translate(0, -50%)';
        break;
      default:
        top = hostRect.top - margin;
        left = hostRect.left + hostRect.width / 2;
        transform = 'translate(-50%, -100%)';
    }

    this.renderer.setStyle(tooltipElement, 'top', `${top}px`, 2);
    this.renderer.setStyle(tooltipElement, 'left', `${left}px`, 2);
    this.renderer.setStyle(tooltipElement, 'transform', transform, 2);

    // Final boundary check for overflow (nudge if necessary)
    this.adjustForViewportBounds(tooltipElement, top, left);

    // Make visible after positioning is finalized
    this.renderer.setStyle(tooltipElement, 'visibility', 'visible', 2);
  }

  private adjustForViewportBounds(
    tooltipElement: HTMLElement,
    top: number,
    left: number
  ): void {
    // Get tooltip dimensions after positioning
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let adjustedTop = top;
    let adjustedLeft = left;

    // Adjust if tooltip goes beyond right edge
    if (tooltipRect.right > viewportWidth) {
      adjustedLeft -= tooltipRect.right - viewportWidth + 10;
    }

    // Adjust if tooltip goes beyond left edge
    if (tooltipRect.left < 0) {
      adjustedLeft += Math.abs(tooltipRect.left) + 10;
    }

    // Adjust if tooltip goes beyond bottom edge
    if (tooltipRect.bottom > viewportHeight) {
      adjustedTop -= tooltipRect.bottom - viewportHeight + 10;
    }

    // Adjust if tooltip goes beyond top edge
    if (tooltipRect.top < 0) {
      adjustedTop += Math.abs(tooltipRect.top) + 10;
    }

    // Set variable offsets so the arrow can counter-shift and point at original target
    this.renderer.setStyle(
      tooltipElement,
      '--arrow-offset-y',
      `${top - adjustedTop}px`,
      2
    );
    this.renderer.setStyle(
      tooltipElement,
      '--arrow-offset-x',
      `${left - adjustedLeft}px`,
      2
    );

    // Apply adjustments if needed
    if (adjustedTop !== top) {
      this.renderer.setStyle(tooltipElement, 'top', `${adjustedTop}px`, 2);
    }
    if (adjustedLeft !== left) {
      this.renderer.setStyle(tooltipElement, 'left', `${adjustedLeft}px`, 2);
    }
  }

  ngOnDestroy(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    this.hideTooltip();
  }
}
